import { AdminRoleId } from '@tassat/common-definitions';
import { currencyAsNumber } from '@tassat/utility';
import moment from 'moment';

export const validateUsdCurrency = value => {
    if (!value || value === '$') {
        return 'Required';
    } else if (!/^\$(\d*,?)+(\.\d{1,2})?$/.test(value)) {
        return 'Invalid amount';
    } else if (parseFloat(value.substr(1)) < parseFloat(0.01)) {
        return 'Value must be greater than $0.00';
    } else if (currencyAsNumber(value)
        .value() > 999999999.99) {
    // Why yes, the maximum value that you may use is $999,999,999.99.
    // There's no way anyone has $1 BILLION DOLLARS!
        return 'Value must be strictly less than $1,000,000,000.00';
    }

    return null;
};

export const validateAccount = value => {
    if (!value) {
        return 'Required';
    } else if (!/^0x([0-9]|[A-F]|[a-f])+$/.test(value)) {
        return 'Account contains invalid characters';
    } else if (value.length !== 42) {
        return 'Account is an invalid length';
    }

    return null;
};

export const validateEmailUniqueness = (email, users) => {
    if (!email) {
        return null;
    }

    const currentEmails = Object.keys(users)
        .map(key => {
            const user = users[key];

            return !user.IsDeleted ? users[key].Email.toLowerCase() : null;
        });

    if (currentEmails.includes(email.toLowerCase())) {
        return 'E-mail is currently in use.  Please enter a unique email.';
    }

    return null;
};

export const validateFullNameUniqueness = (fullName, users, firmId) => {
    if (!fullName || !users || !firmId) {
        return null;
    }

    const firmUserNames = Object.keys(users)
        .map(key => {
            const user = users[key];

            return !user.IsDeleted && user.FirmId === firmId.toString() ? `${user.FirstName.toLowerCase()} ${user.LastName.toLowerCase()}` : null;
        })
        .filter(user => user && !user.IsDeleted);

    if (firmUserNames.includes(fullName.toLowerCase())) {
        return 'A user with this first and last name already exists. Please enter a unique name.';
    }

    return null;
};

export const validateEmailFormatting = (email, required = false) => {
    if (!email && !required) {
        return null;
    } else if (!email) {
        return 'Required';
    }

    const re = new RegExp('^[a-zA-Z0-9_!#$%&\'*+/=?`{|}~^.-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$');

    const correctlyFormatted = re.test(String(email)
        .toLowerCase());

    if (!correctlyFormatted) {
        return 'Please enter a valid e-mail address.';
    }

    if (email.length > 64) {
        return 'E-mail address exceeds number of characters';
    }

    return null;
};

export const validateRequired = value => {
    return value ? null : 'Required';
};

const newFirmUniquenessMessages = {
    'Name': 'A firm with this name already exists.  Please enter a unique name.',
    'Account': 'A firm with this account number already exists. Please enter a unique account number.',
    'ShortName': 'A firm with this short name already exists.  Please enter a unique name.',
    'Email': 'A firm with this email already exists. Please enter a unique email address.'
};

export const validateUniqueness = (field, value, objects) => {
    if (!value) {
        return 'Required';
    }

    var lowercase = value.toLowerCase();

    const notUnique = Object.keys(objects)
        .some(key => {
            return objects[key][field].toLowerCase() === lowercase;
        });
    if (notUnique) {
        return newFirmUniquenessMessages[field];
    }

    return null;
};

export const validateLengthRequirement = (field, length, exact = false) => {
    if (!field) {
        return null;
    }
    if (exact) {
        return field.length === length ? null : `Must be exactly ${length} characters long.`;
    }

    return field.length <= length ? null : `Must be ${length} characters long or shorter.`;
};

export const validateDDALengthRequirement = (field, lengthObj) => {
    if (!field) {
        return null;
    }
    if (lengthObj.ddaLengthMax === lengthObj.ddaLengthMin) {
        return field.length === lengthObj.ddaLengthMax ? null : `Must be exactly ${lengthObj.ddaLengthMax} characters long.`;
    }
    if (field.length < lengthObj.ddaLengthMin)
        return `Must be ${lengthObj.ddaLengthMin} characters long at least.`;
};

export const validateAccountNumberFormatting = (accountNumber) => {
    if (!accountNumber) {
        return 'BD Account number is required';
    }

    if (accountNumber[0] === '0') {
        return 'No leading zeroes on BD Account Number';
    }

    const re = /^\d+$/;
    const correctlyFormatted = re.test(accountNumber);

    if (!correctlyFormatted) {
        return 'BD Account Number can only contain numbers';
    }

    return null;
};

export const validateDescriptionInput = (description, text) => {
    if (!description) {
        return null;
    }

    if (/[|,"_=+!@#$%^&*;:~/\\?<>{}[\]()].*$/.test(description)) {
        return `${text} cannot contain commas, double quotes or special chars.`;
    }

    return null;
};

export const validateNoSpecialCharacters = (description, text = 'Text') => {
    if (!description) {
        return null;
    }

    if (!text.trim()){
        return 'Field cannot be an empty space';
    }

    if (/^[a-zA-Z0-9\-_=+!@#$%^&*;:~ /'?<>{}[\]().]*$/.test(description)) {
        return null;
    }

    return `Invalid ${text}`;
};

//TODO: The validation of strings should be rewritten to take in values.
export const firmNameAndWalletTextValidation = (text) => {
    if (!text) {
        return null;
    }

    if (!text.trim()){
        return 'Field cannot be an empty space';
    }

    if (/^[a-zA-Z0-9\-_=+!@,#$%^&*;:~ /'?{}[\]().]*$/.test(text)) {
        return null;
    }

    return 'Invalid Text';
};

export const validateNoDelimiters = (text) => {
    if (/[,|\\/]+/g.test(text)) {
        return 'Value cannot contain a delimiting characters (",", "|", "\\", "/")';
    }

    return null;
};

export const validatePCGchars = (text) => {
    if (/[+,#<>|"\\/]+/g.test(text)) {
        return 'PCG Name cannot contain a delimiting characters (#,<,>,|,\\,/,",+, and , )';
    }

    return null;
};

export const validateDescriptionFields = (description, text) => {
    if (/[,"/'|\\]+/g.test(description)) {
        return `${text} cannot contain commas, double quotes or special chars.`;
    }

    return null;
};

export const validatePcgUserMappedPcgNumbers = (userValues) => {
    if (userValues.AdminRoleId && userValues.AdminRoleId.value === AdminRoleId.PCG_ADMIN) {
        if (!userValues.PCGMapping || userValues.PCGMapping.length === 0) {
            return 'Must select at least 1 PCG Number for PCG User';
        } else if (userValues.PCGMapping && userValues.PCGMapping.length > 5) {
            return 'Users may only be mapped to a maximum of 5 PCG Numbers';
        }
    } else {
        return null;
    }
};

export const validatePcgAccountMapping = (pcgNumbers) => {
    if (!pcgNumbers || pcgNumbers.length === 0) {
        return 'Must select at least 1 PCG Number for this account';
    } else if (pcgNumbers.length > 3) {
        return 'Account can only have maximum 3 PCG Numbers';
    }

    return null;
};

export const validateDate = (dateValue) => {
    let date = moment(dateValue);
    if (!date.isValid()) {
        return 'Please enter a valid date';
    }

    return null;
};

export const validatePhonenumber = (phoneValue) => {
    if (!/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(phoneValue)) {
        return 'Please enter a valid phone number';
    }

    return null;
};

export const validateAlphaNumeric = (text) => {
    if (!(/^[a-zA-Z0-9]*$/.test(text))) {
        return 'Value must be alphanumeric, (a-z, A-Z, 0-9)';
    }

    return null;
};

export const validateNoLeadingZero = (text) => {
    if (/^0+/.test(text)) {
        return 'Value cannot start with a leading zero';
    }

    return null;
};

export const validateNoWhitespace = (text) => {
    if (/\s+/g.test(text)) {
        return 'Value cannot have whitespace';
    }

    return null;
};

export const validateRequiredStrict = value => {
    if (typeof value === 'string') value = value.trim();

    return value ? null : 'Required';
};

export const validateBasicURL = (text) => {
    if (!/^[a-zA-Z0-9\-_/.]*$/.test(text)) {
        return 'Value must only contain URL allowed characters';
    }
};

export const validateNoHtmlTagCharacters = (value) => {
    if (typeof value === 'string') {
        if (/[<>]/g.test(value)) {
            return 'Characters < and > are not allowed';
        } else {
            return null;
        }
    } else {
        return 'Bad value';
    }
};

export const validateNoExcelFormulaStartingCharacters = (value) => {
    if (typeof value === 'string') {
        const firstChar = value[0];
        if ([ '+', '@', '=', '-' ].includes(firstChar)) {
            return 'First character cannot be +, @, =, -';
        } else {
            return null;
        }
    } else {
        return 'Bad value';
    }
};

export const validateRichText = (value) => {
    //Total character count cannot exceed 255
    if (value.length > 255) {
        return 'Total length of message including styling cannot exceed 255 characters.';
    }

    const isStylyed = /[**|_]/gm.test(value);
    const cleanedValue = value.replace(/[**|_]/gm, '');

    if (isStylyed && cleanedValue.length > 230) {
        return 'Message cannot be greater than 230 characters.';
    }

    const nonPrintableError = validateNoNonPrintable(cleanedValue, true);

    if (/["'|\\]+/g.test(cleanedValue)) {
        return 'Rich text cannot contain quotes or special chars.';
    } else if (nonPrintableError) {
        return nonPrintableError;
    }

    return null;
};

export const validateNoNonPrintable = (value, multiline = false) => {
    //check for multiline text
    // Control characters are the values being validated against here, not a mistaken regex literal.
    // eslint-disable-next-line no-control-regex
    if (multiline && /[\u0000-\u0009]/g.test(value) && /[\u000B-\u000C]/g.test(value) && /[\u000E-\u001F]/g.test(value)) {
        return 'Text can only contain printable characters.';
    }

    // Control characters are the values being validated against here, not a mistaken regex literal.
    // eslint-disable-next-line no-control-regex
    if (!multiline && /[\u0000-\u001F]/g.test(value) ||
    /* Control code Unicode range taken from non-printable (red)
      characters from https://altcodeunicode.com/
    */
    /* Also prevent emojis
      Unicode range taken from https://mothereff.in/regexpu
      and converting /\p{Extended_Pictographic}/u to es5 regexp
    */
        /(?:[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u2388\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2605\u2607-\u2612\u2614-\u2685\u2690-\u2705\u2708-\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763-\u2767\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC00-\uDCFF\uDD0D-\uDD0F\uDD2F\uDD6C-\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDAD-\uDDE5\uDE01-\uDE0F\uDE1A\uDE2F\uDE32-\uDE3A\uDE3C-\uDE3F\uDE49-\uDFFA]|\uD83D[\uDC00-\uDD3D\uDD46-\uDE4F\uDE80-\uDEFF\uDF74-\uDF7F\uDFD5-\uDFFF]|\uD83E[\uDC0C-\uDC0F\uDC48-\uDC4F\uDC5A-\uDC5F\uDC88-\uDC8F\uDCAE-\uDCFF\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDEFF]|\uD83F[\uDC00-\uDFFD])/.test(value)
    ) {
        return 'Text can only contain printable characters.';
    }

    return null;
};

export const validateSingleDoubleQuotes = (description, text) => {
    if (/['"]/gm.test(description)) {
        return `${text} cannot contain single or double quotes.`;
    }

    return null;
};

export const validateNoDelimitersMinusComma = (text, field) => {
    if (/[|\\/]+/g.test(text)) {
        return `${field ? field : 'Value'} cannot contain delimiting characters ("|", "\\", "/")`;
    }

    return null;
};

export const validateEditedValues = (initialValues, values) => {
    //TODO check tests for why 'good data' isn't passing in AdminRoleId correctly
    if (initialValues.AdminRoleId && initialValues.AdminRoleId.value === '5'){
        if (values.FirstName === initialValues.FirstName &&
            values.LastName === initialValues.LastName &&
            values.Phone === initialValues.Phone &&
            values.Notes === initialValues.Notes &&
            values.AdminRoleId.value === initialValues.AdminRoleId.value &&
            values.Notes === initialValues.Notes &&
            JSON.stringify(values.PCGMapping) === JSON.stringify(initialValues.PCGMapping)){
            return 'form not edited';
        }
    } else if (initialValues.AdminRoleId){
        if (values.FirstName === initialValues.FirstName &&
            values.LastName === initialValues.LastName &&
            values.Phone === initialValues.Phone &&
            values.Notes === initialValues.Notes &&
            values.AdminRoleId.value === initialValues.AdminRoleId.value &&
            values.Notes === initialValues.Notes){
            return 'form not edited';
        }
    }

    return null;
};

export const validateNumbersOnly = (value) => {
    return /\D/.test(value) ? 'Text can only contain numbers' : null;
};